import isoCountries from 'i18n-iso-countries';
import frCountries from 'i18n-iso-countries/langs/fr.json';
import {
    type CountryCode,
    parsePhoneNumberFromString,
    formatIncompletePhoneNumber,
    validatePhoneNumberLength,
    getCountries,
    getCountryCallingCode,
    type ValidatePhoneNumberLengthResult
} from 'libphonenumber-js/max';

export interface CountryPhoneOption {
    code: CountryCode;
    name: string;
    prefix: string;
}

export default class PhoneNumber {
    public readonly raw: string;
    public readonly fixed: string;
    public readonly international: string | undefined;
    public readonly national: string | undefined;
    public readonly inputFormatted: string | undefined;
    public readonly printFormattedNational: string | undefined;
    public readonly printFormattedInternational: string | undefined;
    public readonly country: CountryCode;
    public readonly isValid: boolean = false;
    public readonly isPossible: boolean = false;

    constructor(rawNumber: string, initialCountry?: CountryCode) {
        this.raw = rawNumber;

        // First, fixed the number quickly (only digits and "+" allowed at start only)
        this.fixed = rawNumber.replace(/(?!^)\D|(?<!^)\+/g, '');

        // Get possible countries and compare to the provided country to choose
        let parsedForCountry = parsePhoneNumberFromString(this.fixed, initialCountry);
        this.country = initialCountry ?? 'FR';
        if (parsedForCountry) {
            const possibleCountries = parsedForCountry.getPossibleCountries();
            if (possibleCountries?.length && (!initialCountry || !possibleCountries.includes(initialCountry))) {
                this.country = parsedForCountry?.getPossibleCountries()[0];
            }
        }

        // Fix number definitively for parsing
        this.fixed = PhoneNumber.truncateToMaxLength(this.fixed, this.country);

        // Parse number with chosen country
        const parsedForFormats = parsePhoneNumberFromString(this.fixed, this.country);
        if (!parsedForFormats) {
            return;
        }

        // Define properties
        this.isValid = parsedForFormats.isValid();
        this.isPossible = parsedForFormats.isPossible();
        this.international = parsedForFormats.number;
        this.national = parsedForFormats.nationalNumber;
        this.inputFormatted = this.isValid ?
            parsedForFormats.formatNational() :
            formatIncompletePhoneNumber(this.national, this.country);
        this.printFormattedNational = this.isPossible ?
            parsedForFormats.formatNational() :
            formatIncompletePhoneNumber(this.national, this.country);
        this.printFormattedInternational = this.isPossible ?
            parsedForFormats.formatInternational() :
            formatIncompletePhoneNumber(this.international, this.country);
    }

    private static truncateToMaxLength(number: string, country: CountryCode): string {
        let validLengthStatus = validatePhoneNumberLength(number, country) as ValidatePhoneNumberLengthResult;
        let truncated = number;
        while (validLengthStatus === 'TOO_LONG') {
            truncated = truncated.slice(0, -1);
            validLengthStatus = validatePhoneNumberLength(truncated, country) as ValidatePhoneNumberLengthResult;
        }
        return truncated;
    }

    public static getCountryPhoneOptions(): Record<CountryCode, CountryPhoneOption> {
        const countries = getCountries();
        const result: Record<CountryCode, CountryPhoneOption> = {} as Record<CountryCode, CountryPhoneOption>;
        isoCountries.registerLocale(frCountries);
        for (const country of countries) {
            try {
                const name = isoCountries.getName(country, 'fr');
                const prefix = getCountryCallingCode(country);
                if (name && prefix) {
                    result[country] = { code: country, name, prefix: `+${ prefix }` };
                }
            } catch (err) {
                // do nothing
            }
        }
        const sortedEntries = Object.entries(result)
            .sort(([, a], [, b]) => a.name.localeCompare(b.name));
        return Object.fromEntries(sortedEntries) as Record<CountryCode, CountryPhoneOption>;
    }

}
